import { useMemo } from 'react';

import { Cluster } from '@cast/types';

import { StorageOptimizationState } from '@cast/types-stop';
import { NodeWithDependencies } from 'features/cluster/nodes/types';

import { useNodeStorageSummaryQuery } from 'hooks/queries/nodes/useNodeStorageSummaryQuery';
import { useStorageActionsQuery } from 'hooks/queries/storage-autoscaler';
import { StorageOptimizationActiveBanner } from './_components/StorageOptimizationActiveBanner';

import { useNodeStorageTimeRangeSelection } from './_hooks/useNodeStorageTimeRangeSelection';
import { ManagedDiskSection } from './ManagedDiskSection';
import { OptimizationActionsSection } from './OptimizationActionsSection';
import { RootDiskSection } from './RootDiskSection';
import { StorageRangePicker } from './StorageRangePicker';
import { Stack } from '@cast/design-system';

type StorageTabContentProps = {
  node?: NodeWithDependencies;
  cluster: Cluster;
  isNodeLoading: boolean;
  storageState: StorageOptimizationState;
};

export const StorageTabContent = ({
  node,
  cluster,
  isNodeLoading,
  storageState,
}: StorageTabContentProps) => {
  const { getRange, key } = useNodeStorageTimeRangeSelection();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const { from, to } = useMemo(() => getRange(), [key]);

  const {
    storageActions,
    storageActionsSummary,
    isLoading,
    isFetching,
    totalCount,
    refetch,
    error,
  } = useStorageActionsQuery({
    enabled: !!node,
    clusterId: cluster.id,
    nodeName: node?.name,
    startTime: from,
    endTime: to,
    pageSize: 3,
  });

  const {
    managedEphemeralDisks,
    rootDisk,
    isLoading: isLoadingStorageSummary,
    error: errorStorageSummary,
    refetch: refetchStorageSummary,
  } = useNodeStorageSummaryQuery({
    clusterId: cluster.id,
    nodeName: node?.name,
  });

  return (
    <Stack gap={16}>
      {storageState?.ephemeralAgentInstalled && (
        <StorageOptimizationActiveBanner />
      )}

      <Stack direction="row" alignItems="center" justifyContent="flex-end">
        <StorageRangePicker />
      </Stack>

      <Stack direction="column" gap={32}>
        {storageState?.ephemeralAgentInstalled && (
          <OptimizationActionsSection
            storageActions={storageActions}
            storageActionsSummary={storageActionsSummary}
            isLoading={isLoading || isNodeLoading || isFetching}
            totalCount={totalCount}
            error={error}
            refetch={refetch}
            nodeName={node?.name}
          />
        )}

        <RootDiskSection
          node={node}
          cluster={cluster}
          rootDisk={rootDisk}
          isNodeLoading={isNodeLoading}
          isCloudDisksEnabled={storageState?.cloudDisksEnabled}
        />

        {storageState?.ephemeralAgentInstalled && (
          <ManagedDiskSection
            node={node}
            cluster={cluster}
            disks={managedEphemeralDisks}
            isLoading={isLoadingStorageSummary || isNodeLoading}
            error={errorStorageSummary}
            refetch={refetchStorageSummary}
            isCloudDisksEnabled={storageState?.cloudDisksEnabled}
          />
        )}
      </Stack>
    </Stack>
  );
};
